import { useRef, useState, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isAccountant, isAdmin, isClericalOver, isManagerOver } from "src/Common/function";

const tabUrlArray = [
  "/settings/account",
  "/settings/notification",
  "/settings/company",
  "/settings/vendor",
  "/settings/report",
  "/settings/branch",
  "/settings/materials",
  "/settings/services",
];

function TabBar() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeIndex = tabUrlArray.findIndex((url) => location.pathname.startsWith(url));

  const tabClicked = (index) => {
    navigate(tabUrlArray[index]);
  };

  // Tabs are role-gated, so the visible set varies; keep each tab keyed to its
  // url index so the underline lines up regardless of which tabs are hidden.
  const tabs = [
    { index: 0, label: "Account", visible: true },
    { index: 1, label: "Notification Center", visible: isManagerOver() },
    { index: 2, label: "Company", visible: isAdmin() },
    { index: 3, label: "Vendor", visible: isClericalOver() || isAccountant() },
    { index: 5, label: "Branch", visible: isAdmin() },
    { index: 6, label: "Materials", visible: isAdmin() },
    { index: 7, label: "Services", visible: isManagerOver() },
  ].filter((tab) => tab.visible);

  const tabRefs = useRef({});
  const [tabUnderline, setTabUnderline] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const measureUnderline = () => {
      const activeTab = tabRefs.current[activeIndex];
      if (activeTab) {
        setTabUnderline({ left: activeTab.offsetLeft, width: activeTab.offsetWidth });
      }
    };

    measureUnderline();

    // Re-measure once webfonts finish loading: on a fresh page load the custom
    // font may not be ready when we first measure, so the tab text reflows after
    // and the underline ends up mispositioned.
    if (document.fonts?.ready) {
      document.fonts.ready.then(measureUnderline);
    }

    window.addEventListener("resize", measureUnderline);
    return () => window.removeEventListener("resize", measureUnderline);
  }, [activeIndex, tabs.length]);

  return (
    <div className="flex items-center justify-between border-b border-[#E5E8F3] flex-shrink-0 px-7">
      <div className="relative flex items-center gap-5 py-3 mt-3">
        {tabs.map((tab) => (
          <span
            key={tab.index}
            ref={(el) => (tabRefs.current[tab.index] = el)}
            onClick={() => tabClicked(tab.index)}
            className={`font-OpenSansRegular text-[14px] font-[600] leading-[20px] tracking-[0.14px] text-center capitalize cursor-pointer pb-3 -mb-3 transition-colors duration-300 ${
              activeIndex === tab.index ? "text-[#0058E5]" : "text-[#7C829A]"
            }`}
          >
            {tab.label}
          </span>
        ))}
        <span
          className="absolute bottom-0 h-[2px] bg-[#0058E5] transition-all duration-300 ease-out"
          style={{
            left: tabUnderline.left,
            width: tabUnderline.width,
          }}
        />
      </div>
    </div>
  );
}

export default TabBar;
